import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Box, AppBar, CssBaseline, Drawer, Toolbar, Typography, IconButton, Menu, MenuItem, Badge, Tooltip } from '@mui/material';
import Sidebar from './Sidebar';
import Router from './Router';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';

const drawerWidth = 270;
const AppbarHeight = 64;

const Frame = () => {
    const navigate = useNavigate();
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userFirstName = user?.userFirstName || 'User';
    const userLastName = user?.userLastName || '';

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const { logout } = useContext(AuthContext);
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchUnreadCount = useCallback(async () => {
        try {
            if (document.hidden) return; // Skip polling if tab is inactive
            const token = sessionStorage.getItem('token');
            if (!token) return;
            const decoded = jwtDecode(token);
            const level = decoded.level || 1;
            const uId = decoded.userId || '';

            const res = await axios.get(`${API_BASE_URL}/user-messages/unread-count`, {
                params: { level, userId: uId },
            });
            setUnreadCount(res.data.unreadCount || 0);
        } catch (err) {
            // Ignore rate limit or network errors silently
        }
    }, []);

    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 45000); // Check every 45 seconds
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleProfileClick = () => {
        navigate('/profile');
        handleMenuClose();
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: `calc(100% - ${drawerWidth}px)`,
                    ml: `${drawerWidth}px`,
                    height: AppbarHeight,
                    background: 'linear-gradient(135deg, #FFA500 0%, #ff8c00 100%)',
                    boxShadow: '0 4px 15px rgba(255, 165, 0, 0.25)',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <Toolbar sx={{ height: AppbarHeight, px: 3 }}>
                    <Typography variant="h5" noWrap component="div" sx={{ flexGrow: 1, color: '#ffffff', fontWeight: 700, fontSize: '1.25rem' }}>
                        Welcome, {userFirstName} {userLastName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Tooltip title="User Messages & Notifications">
                            <IconButton
                                onClick={() => navigate('/messages')}
                                sx={{
                                    color: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    },
                                    p: 1,
                                }}
                            >
                                <Badge badgeContent={unreadCount} color="error" max={99}>
                                    <EmailIcon sx={{ fontSize: 24, color: '#ffffff' }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <IconButton
                            edge="end"
                            aria-label="profile"
                            aria-controls="profile-menu"
                            aria-haspopup="true"
                            onClick={handleMenuClick}
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                },
                                p: 0.8,
                            }}
                        >
                            <AccountCircleIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    </Box>
                    <Menu
                        id="profile-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            elevation: 3,
                            sx: {
                                mt: 1.5,
                                borderRadius: 3,
                                minWidth: 180,
                                p: 0.5,
                            },
                        }}
                    >
                        <MenuItem onClick={handleProfileClick} sx={{ borderRadius: 1.5, py: 1 }}>
                            <PersonIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Profile</Typography>
                        </MenuItem>
                        <MenuItem onClick={logout} sx={{ borderRadius: 1.5, py: 1, color: 'error.main' }}>
                            <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Logout</Typography>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        borderRight: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        boxShadow: '2px 0 10px rgba(0,0,0,0.03)',
                    },
                }}
            >
                <Box
                    sx={{
                        height: AppbarHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justify: 'center',
                        px: 3,
                        background: 'linear-gradient(135deg, #FFA500 0%, #ff8c00 100%)',
                        color: 'white',
                    }}
                >
                    <AccountBalanceWalletIcon sx={{ fontSize: 30, mr: 1.5, color: 'white' }} />
                    <Typography
                        variant="h5"
                        noWrap
                        sx={{
                            fontWeight: 800,
                            letterSpacing: '0.02em',
                            color: 'white',
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                    >
                        Q Pay
                    </Typography>
                </Box>
                <Sidebar />
            </Drawer>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: 'background.default',
                    p: { xs: 2, sm: 3 },
                    width: `calc(100% - ${drawerWidth}px)`,
                    mt: `${AppbarHeight}px`,
                    minHeight: `calc(100vh - ${AppbarHeight}px)`,
                    overflowX: 'hidden',
                }}
            >
                <Router />
            </Box>
        </Box>
    );
};

export default Frame;
